import { Controller } from "@hotwired/stimulus"
import { debounce } from "../application"

// We'll access Alpine globally if it's available
// This avoids import errors if Alpine is loaded separately

export default class extends Controller {
    static targets = ["textarea", "userList"]
    static values = {
        feedItemId: String,
        disableHighlighting: { type: Boolean, default: false }
    }

    connect() {
        this.comment = ""
        this.searchTerm = ""
        this.showUserList = false
        this.cursorPosition = 0
        this.atPosition = -1
        this.mentionSpans = []
        this.userList = []
        this.currentUserIndex = -1
        this.isUserListVisible = false

        // Create debounced search function
        this.debouncedSearch = debounce((term) => {
            this.searchUsers(term)
        }, 200)

        // Store bound event handlers so we can remove them later
        this.boundHandleInput = this.handleInput.bind(this)
        this.boundHandleKeyUp = this.handleKeyUp.bind(this)
        this.boundHandleReplyEvent = this.handleReplyEvent.bind(this)
        this.boundHandleResize = this.handleResize.bind(this)

        // Attach event listeners
        this.attachEventListeners()

        // Initialize highlighting if there's existing content and highlighting is enabled
        if (this.textareaTarget.value && !this.disableHighlightingValue) {
            this.comment = this.textareaTarget.value
            this.highlightMentions()
        }

        // Set initial positioning for user list
        this.positionUserList()

        // Listen for global reply events
        document.addEventListener('skinsort:reply-to-comment', this.boundHandleReplyEvent)

        // Listen for window resize events
        window.addEventListener('resize', this.boundHandleResize)

        // Initialize textarea height
        this.adjustTextareaHeight()
    }

    disconnect() {
        // Clean up event listeners when controller is disconnected
        this.removeEventListeners()

        // Remove global event listener
        document.removeEventListener('skinsort:reply-to-comment', this.boundHandleReplyEvent)

        // Remove resize event listener
        window.removeEventListener('resize', this.boundHandleResize)
    }

    attachEventListeners() {
        // Add event listeners to the textarea
        this.textareaTarget.addEventListener("input", this.boundHandleInput)
        this.textareaTarget.addEventListener("keyup", this.boundHandleKeyUp)
    }

    removeEventListeners() {
        // Only try to remove if the textarea target still exists
        if (this.hasTextareaTarget) {
            this.textareaTarget.removeEventListener("input", this.boundHandleInput)
            this.textareaTarget.removeEventListener("keyup", this.boundHandleKeyUp)
        }
    }

    // Handle the global reply event
    handleReplyEvent(event) {
        const { username, commentId } = event.detail
        this.handleReplyToComment(username, commentId)
    }

    handleInput(event) {
        this.comment = event.target.value
        this.checkForMentions(this.comment)
        if (!this.disableHighlightingValue) {
            this.highlightMentions()
        }
        this.adjustTextareaHeight()
    }

    handleKeyUp(event) {
        // Additional keyup handling if needed
    }

    checkForMentions(text) {
        const cursorPos = this.textareaTarget.selectionStart
        this.cursorPosition = cursorPos

        // Find the position of @ before cursor
        const textBeforeCursor = text.substring(0, cursorPos)
        const lastAtPos = textBeforeCursor.lastIndexOf('@')

        if (lastAtPos !== -1 && (lastAtPos === 0 || text[lastAtPos - 1] === ' ')) {
            const searchText = textBeforeCursor.substring(lastAtPos + 1)

            // Only search if there's no space after @
            if (!searchText.includes(' ')) {
                this.atPosition = lastAtPos
                this.searchTerm = searchText
                this.debouncedSearch(searchText)
                return
            }
        }

        // Hide user list if no @ is found or if there's a space after @
        this.hideUserList()
        this.atPosition = -1
    }

    searchUsers(term) {
        if (term.length === 0) {
            this.hideUserList()
            return
        }

        // Build the URL with feed_item_id if available
        let url = `/api/users/search?term=${encodeURIComponent(term)}`
        if (this.hasFeedItemIdValue && this.feedItemIdValue) {
            url += `&feed_item_id=${this.feedItemIdValue}`
        }

        // Fetch users matching the search term
        fetch(url)
            .then(response => response.text())
            .then(html => {
                if (html.trim()) {
                    // First position the user list before showing it
                    this.positionUserList()

                    // Then update content and show
                    this.userListTarget.innerHTML = html
                    this.userListTarget.classList.remove("hidden")
                } else {
                    this.hideUserList()
                }
            })
            .catch(error => {
                console.error("Error fetching users:", error)
                this.hideUserList()
            })
    }

    // New method to position the user list based on device type
    positionUserList() {
        // Check if the device is mobile (max-width: 768px is a common breakpoint for mobile)
        const isMobile = window.innerWidth <= 768

        // Always ensure left-0 is applied
        this.userListTarget.classList.add("left-0")

        if (isMobile) {
            // Position above the textarea on mobile
            this.userListTarget.classList.remove("top-full", "mt-1")
            this.userListTarget.classList.add("bottom-full", "mb-1")
        } else {
            // Position below the textarea on desktop (default)
            this.userListTarget.classList.remove("bottom-full", "mb-1")
            this.userListTarget.classList.add("top-full", "mt-1")
        }
    }

    hideUserList() {
        this.userListTarget.classList.add("hidden")
        this.userListTarget.innerHTML = ""
    }

    highlightMentions() {
        const text = this.textareaTarget.value
        const mentionRegex = /@[a-zA-Z0-9_\.\-]+/g
        let match
        let lastIndex = 0
        let html = ''

        // Build highlighted HTML
        while ((match = mentionRegex.exec(text)) !== null) {
            // Add the text before the mention
            html += this.escapeHtml(text.slice(lastIndex, match.index))
            // Add the highlighted mention with proper class
            html += `<span class="mention">${this.escapeHtml(match[0])}</span>`
            lastIndex = mentionRegex.lastIndex
        }
        // Add any remaining text
        html += this.escapeHtml(text.slice(lastIndex))
        // Add trailing space to match textarea behavior
        html += '\n'

        // Check if using CSS Grid approach (Fizzy-style)
        const wrapper = this.textareaTarget.parentNode
        const useGridApproach = wrapper.classList.contains('mention-textarea-wrapper')

        let backdropDiv = wrapper.querySelector('.mention-backdrop')

        if (useGridApproach) {
            // CSS Grid approach - create backdrop if needed
            if (!backdropDiv) {
                backdropDiv = document.createElement('div')
                backdropDiv.classList.add('mention-backdrop')
                // Copy only padding from textarea - CSS handles font inheritance
                const style = window.getComputedStyle(this.textareaTarget)
                backdropDiv.style.padding = style.padding
                wrapper.insertBefore(backdropDiv, this.textareaTarget)
            }
            backdropDiv.innerHTML = html
        } else {
            // Legacy approach - absolute positioning
            let highlightDiv = this.textareaTarget.previousElementSibling
            if (!highlightDiv || !highlightDiv.classList.contains('mention-highlight')) {
                highlightDiv = document.createElement('div')
                highlightDiv.classList.add('mention-highlight', 'absolute', 'pointer-events-none', 'whitespace-pre-wrap', 'text-sm')
                highlightDiv.style.top = '0'
                highlightDiv.style.left = '0'
                highlightDiv.style.right = '0'
                highlightDiv.style.bottom = '0'
                highlightDiv.style.padding = window.getComputedStyle(this.textareaTarget).padding
                highlightDiv.style.zIndex = '1'
                highlightDiv.style.lineHeight = window.getComputedStyle(this.textareaTarget).lineHeight
                highlightDiv.style.wordBreak = 'break-word'
                this.textareaTarget.parentNode.insertBefore(highlightDiv, this.textareaTarget)

                // Position the textarea above the highlight div
                this.textareaTarget.style.position = 'relative'
                this.textareaTarget.style.zIndex = '2'
            }

            // Update the highlight div with legacy styling
            highlightDiv.innerHTML = html.replace(/class="mention"/g, 'class="text-indigo-800"')
            highlightDiv.style.color = 'rgb(55, 65, 81)'

            // Make textarea text transparent but keep the caret visible
            this.textareaTarget.style.color = 'transparent'
            this.textareaTarget.style.caretColor = 'rgb(55, 65, 81)'
            this.textareaTarget.style.backgroundColor = 'transparent'
        }
    }

    // Helper method to escape HTML special characters
    escapeHtml(text) {
        const div = document.createElement('div')
        div.textContent = text
        return div.innerHTML
    }

    insertMention(event) {
        event.preventDefault()
        const username = event.currentTarget.dataset.username

        if (this.atPosition === -1) return

        const beforeMention = this.comment.substring(0, this.atPosition)
        const afterMention = this.comment.substring(this.cursorPosition)
        const newComment = beforeMention + '@' + username + ' ' + afterMention
        const newCursorPos = this.atPosition + username.length + 2 // +2 for @ and space

        // Update our internal state
        this.comment = newComment

        // Hide the user list before modifying the textarea
        this.hideUserList()

        // Update the textarea and maintain focus
        this.textareaTarget.focus()
        this.textareaTarget.value = newComment
        this.textareaTarget.setSelectionRange(newCursorPos, newCursorPos)

        // Sync with Alpine.js x-model after setting cursor position
        this.textareaTarget.dispatchEvent(new Event('input', { bubbles: true }))

        // Update highlighting after all other operations (if enabled)
        if (!this.disableHighlightingValue) {
            this.highlightMentions()
        }
    }

    // New method to handle reply functionality
    handleReplyToComment(username, commentId) {
        try {
            // Get the textarea
            const textarea = this.textareaTarget

            // Set focus to textarea
            textarea.focus()

            // Find the Alpine component container
            const alpineContainer = document.getElementById('comment-form-container')

            // Guard against double-tap: if the mention is already at the start, don't prepend again
            const mentionPrefix = `@${username} `
            if (textarea.value.startsWith(mentionPrefix) || textarea.value.trim() === `@${username}`) {
                return
            }

            // Insert @username at the start if textarea is empty
            const newValue = textarea.value.trim() === '' ?
                `@${username} ` :
                `@${username} ${textarea.value}`

            // Update the textarea value
            textarea.value = newValue

            // Update Alpine.js state if available
            if (alpineContainer) {
                // Call the Alpine.js setComment method
                if (typeof alpineContainer.__x !== 'undefined') {
                    alpineContainer.__x.$data.setComment(newValue)
                } else {
                    textarea.dispatchEvent(new Event('input', { bubbles: true }))
                }
            } else {
                textarea.dispatchEvent(new Event('input', { bubbles: true }))
            }

            // Find and update the hidden reply_to_comment_id field
            const form = textarea.closest('form')
            const replyToCommentIdField = form.querySelector('input[name="feed_item_comment[reply_to_comment_id]"]')
            if (replyToCommentIdField) {
                replyToCommentIdField.value = commentId
            }

            // Scroll the textarea into view
            textarea.scrollIntoView({ behavior: 'smooth', block: 'center' })

            // Update our internal state
            this.comment = newValue

            // Update highlighting (if enabled)
            if (!this.disableHighlightingValue) {
                this.highlightMentions()
            }
        } catch (error) {
            console.error("Error in handleReplyToComment:", error)
        }
    }

    // Keep the original method for backward compatibility
    replyToComment(event) {
        const username = event.currentTarget.dataset.username
        const commentId = event.currentTarget.dataset.commentId

        this.handleReplyToComment(username, commentId)
    }

    // Handle window resize events
    handleResize() {
        // Only reposition if the user list is visible
        if (!this.userListTarget.classList.contains("hidden")) {
            this.positionUserList()
        }
    }

    // Add new method for auto-resizing textarea
    adjustTextareaHeight() {
        const textarea = this.textareaTarget
        const wrapper = textarea.parentNode
        const useGridApproach = wrapper.classList.contains('mention-textarea-wrapper')

        // Reset height to auto to get the correct scrollHeight
        textarea.style.height = 'auto'
        // Set the height to scrollHeight to fit content
        textarea.style.height = `${textarea.scrollHeight}px`

        if (useGridApproach) {
            // CSS Grid handles synchronization automatically
            // Just update backdrop height if it exists
            const backdropDiv = wrapper.querySelector('.mention-backdrop')
            if (backdropDiv) {
                backdropDiv.style.height = `${textarea.scrollHeight}px`
            }
        } else {
            // Legacy approach - update highlight div height
            const highlightDiv = textarea.previousElementSibling
            if (highlightDiv && highlightDiv.classList.contains('mention-highlight')) {
                highlightDiv.style.height = `${textarea.scrollHeight}px`
            }
        }
    }
} 